<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { useTagNameSuggestions } from '@/composables/useTagNameSuggestions'
import HashtagSuggestions from './HashtagSuggestions.vue'

// A plain field for one tag name (the filter bar, the tag settings) with the same suggestion list as the add fields: the most
// used candidates when it is empty, narrowed by what is typed. Picking one (tap, Enter or Tab on a selected entry) emits
// `pick`; Enter on typed text emits `submit`, so the page keeps what it did with a plain input before. `escape` is only
// emitted when the list was closed already.
const props = withDefaults(
  defineProps<{
    modelValue?: string // (a draft that is not there yet counts as empty)
    names: string[] // (the candidates)
    known?: string[] // (the candidates, most used first; else in the given order)
    placeholder?: string
    ariaLabel?: string
    enterkeyhint?: 'done' | 'enter' | 'go' | 'next' | 'search' | 'send'
    placement?: 'above' | 'below'
  }>(),
  {
    modelValue: undefined,
    known: undefined,
    placeholder: undefined,
    ariaLabel: undefined,
    enterkeyhint: 'done',
    placement: 'below',
  },
)
const emit = defineEmits<{
  'update:modelValue': [value: string]
  pick: [name: string]
  submit: []
  focus: []
  blur: [event: FocusEvent]
  escape: [event: KeyboardEvent]
}>()

const id = useId()
const value = computed(() => props.modelValue ?? '')
const focused = ref(false)
const input = ref<HTMLInputElement | null>(null)
const suggestions = useTagNameSuggestions({
  value,
  focused,
  names: () => props.names,
  known: () => props.known ?? props.names,
})
const { matches, active, open } = suggestions
const optionId = (index: number): string => `${id}-option-${index}`

function pick(name: string): void {
  suggestions.reset()
  emit('pick', name)
}

function onInput(event: Event): void {
  suggestions.typed()
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}

function onEnter(event: KeyboardEvent): void {
  event.preventDefault()
  const name = suggestions.accept()
  if (name !== null) pick(name)
  else emit('submit')
}

function onTab(event: KeyboardEvent): void {
  const name = suggestions.onTab(event)
  if (name !== null) pick(name)
}

function onEscape(event: KeyboardEvent): void {
  suggestions.onEscape(event)
  // (an Escape that only closed the list is not for the page around it)
  if (!event.cancelBubble) emit('escape', event)
}

defineExpose({ focus: () => input.value?.focus() })
</script>

<template>
  <span class="tag-name-input">
    <input
      ref="input"
      class="ui-input"
      type="text"
      role="combobox"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
      :value="value"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      :enterkeyhint="enterkeyhint"
      aria-autocomplete="list"
      :aria-expanded="open"
      :aria-controls="`${id}-list`"
      :aria-activedescendant="active >= 0 ? optionId(active) : undefined"
      @input="onInput"
      @focus="((focused = true), emit('focus'))"
      @blur="((focused = false), emit('blur', $event))"
      @keydown.down="suggestions.move(1, $event)"
      @keydown.up="suggestions.move(-1, $event)"
      @keydown.enter="onEnter"
      @keydown.tab="onTab"
      @keydown.esc="onEscape"
    />
    <HashtagSuggestions
      :matches="matches"
      :active="active"
      :open="open"
      :placement="placement"
      :list-id="`${id}-list`"
      :option-id="optionId"
      @pick="pick"
    />
  </span>
</template>

<style scoped>
.tag-name-input {
  position: relative;
  display: block;
  min-width: 0;
}
</style>
